import { I18n } from "@lingui/core";
import clamp from "date-fns/clamp";
import eachDayOfInterval from "date-fns/eachDayOfInterval";
import eachWeekOfInterval from "date-fns/eachWeekOfInterval";
import endOfMonth from "date-fns/endOfMonth";
import endOfWeek from "date-fns/endOfWeek";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isToday from "date-fns/isToday";
import isValid from "date-fns/isValid";
import startOfMonth from "date-fns/startOfMonth";
import startOfWeek from "date-fns/startOfWeek";
import { SupportedLocales } from "../../../i18n/constants";
import { DatePickerFrameProps } from "../dialog/components/DatePickerFrame";
import { DatePair, DatePickerDate, InternalRange } from "../types";

/**
 * Adapted from https://gist.github.com/miljan-aleksic/bd70452a3f0cd6a11545db9f6ab57df6
 */
export const getMonthMatrix = (date: Date) => {
    const matrix = eachWeekOfInterval({
        start: startOfMonth(date),
        end: endOfMonth(date),
    });

    return matrix.flatMap((weekDay) =>
        eachDayOfInterval({
            start: startOfWeek(weekDay),
            end: endOfWeek(weekDay),
        })
    );
};

/**
 * @returns today's date, in the current time zone, stripped of any hours, minutes, seconds,
 * or sub-seconds.
 */
export const getToday = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

/**
 * @returns A javascript Date object for the given DatePickerDate object. This will be in the
 * current time zone, stripped of any hours, minutes, seconds, or sub-seconds.
 */
export const toDate = (pickerDate: DatePickerDate): Date => new Date(pickerDate.year, pickerDate.month, pickerDate.day);

/**
 * @returns a DatePickerDate object from the given javascript Date object.
 */
export const toDatePickerDate = (date: Date): DatePickerDate => ({
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
});

const pad = (num: number): string => {
    let str = String(num);
    let i = -1;
    const length = 2 - str.length;
    while (++i < length) {
        str = "0" + str;
    }
    return str;
};

export const isValidDate = (date: DatePickerDate): boolean => {
    if (!date) {
        return false;
    }

    // Add 1 to month here for the ISO date format constructor call.
    const parsed = new Date(`${date.year}-${pad(date.month + 1)}-${pad(date.day)}T00:00:00Z`);

    return (
        isValid(parsed) &&
        // Ensure there is no overflow on the month or day.
        date.day === parsed.getUTCDate() &&
        date.month === parsed.getUTCMonth() &&
        date.year === parsed.getUTCFullYear()
    );
};

export const isDayDisabled = (
    day: Date,
    dayIsDisabled: DatePickerFrameProps["dayIsDisabled"],
    minDate?: DatePickerDate,
    maxDate?: DatePickerDate
): boolean => {
    if (minDate && isBefore(day, toDate(minDate))) {
        return true;
    }
    if (maxDate && isAfter(day, toDate(maxDate))) {
        return true;
    }

    return dayIsDisabled?.(toDatePickerDate(day)) ?? false;
};

export const isPartialRange = (range: InternalRange): range is [Date, null] =>
    Array.isArray(range) && range[0] !== null && range[1] === null;

const isDatePickerDate = (date: Date | DatePickerDate): date is DatePickerDate => {
    return date && Object.hasOwnProperty.call(date, "year");
};

export const isFullRange = (range: InternalRange): range is DatePair =>
    Array.isArray(range) && range[0] !== null && range[1] !== null;

export const formatDayLabel = (locale: SupportedLocales, date: Date | DatePickerDate): string => {
    return (isDatePickerDate(date) ? toDate(date) : date).toLocaleString(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
    });
};

export const getSinglePickerDayLabel = (i18n: I18n, locale: SupportedLocales, day: Date) => {
    if (isToday(day)) {
        return i18n._(
            /*i18n*/ {
                id: "Today, {dateStr}",
                values: { dateStr: formatDayLabel(locale, day) },
                comment: "This is the alt label for the button on the date picker for today's date",
            }
        );
    } else {
        return formatDayLabel(locale, day);
    }
};

export const getRangePickerDayLabel = (i18n: I18n, locale: SupportedLocales, day: Date, range: InternalRange) => {
    if (range === null || isFullRange(range)) {
        return i18n._(
            /*i18n*/ {
                id: "{dateStr}, select start date",
                values: { dateStr: formatDayLabel(locale, day) },
                comment:
                    "This is the alt text in the date range picker for any date button before a start date has been chosen. " +
                    "The comma here is important as it provides a little pause for a screen reader",
            }
        );
    } else {
        return i18n._(
            /*i18n*/ {
                id: "{dateStr}, select end date",
                values: { dateStr: formatDayLabel(locale, day) },
                comment:
                    "This is the alt text in the date range picker for any date button after a start date has been chosen. " +
                    "The comma here is important as it provides a little pause for a screen reader",
            }
        );
    }
};

/**
 * @returns the visible date to use for the date picker. This will be the provided date if it's
 * valid, the initialPickerDate if it's valid, or today's date if neither of those are valid. This
 * is used to determine the initial visible date for the date picker from the props provided. It
 * also clamps the returned date to be within the min/max dates.
 */
export const getVisibleDateFromProps = (
    value: DatePickerDate | null,
    initialPickerDate: DatePickerDate | null,
    minDate: DatePickerDate | null,
    maxDate: DatePickerDate | null
): Date => {
    let dateToUse: DatePickerDate | null = null;
    if (value !== null && isValidDate(value)) {
        // If there's a selected value, use that.
        dateToUse = value;
    } else if (initialPickerDate) {
        // If there's an initialPickerDate, use that.
        dateToUse = initialPickerDate;
    } else {
        // otherwise, use today.
        dateToUse = toDatePickerDate(getToday());
    }
    // Always clamp this selection to be within the min/max dates.
    const clamped = clampDate(dateToUse, minDate, maxDate);
    return clamped;
};

const MIN_DATE = new Date(-8640000000000000);
const MAX_DATE = new Date(8640000000000000);
export const clampDate = (date: DatePickerDate, minDate: DatePickerDate | null, maxDate: DatePickerDate | null) =>
    clamp(toDate(date), {
        start: minDate ? toDate(minDate) : MIN_DATE,
        end: maxDate ? toDate(maxDate) : MAX_DATE,
    });
